import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, History, ExternalLink } from "lucide-react";
import { z } from "zod";
import { formatDistanceToNow } from "date-fns";

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  phone_verified: boolean | null;
  avatar_url: string | null;
  created_at: string;
}

interface UserWithProfile {
  id: string;
  email: string;
  created_at: string;
  profile: UserProfile | null;
  roles: string[];
}

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithProfile;
  onUserUpdated: () => void;
}

const userFormSchema = z.object({
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  first_name: z.string().trim().min(1, "First name is required").max(100, "First name must be less than 100 characters"),
  last_name: z.string().trim().min(1, "Last name is required").max(100, "Last name must be less than 100 characters"),
  phone: z.string().trim().regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number format (use E.164 format: +16195551234)").optional().or(z.literal("")),
});

export function EditUserDialog({ open, onOpenChange, user, onUserUpdated }: EditUserDialogProps) {
  const [loading, setLoading] = useState(false);
  
  // Role state
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTechnician, setIsTechnician] = useState(false);
  
  // Profile state
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  
  // Activity tab state
  const [activeTab, setActiveTab] = useState("profile");
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    if (user) {
      // Initialize roles
      setIsAdmin(user.roles.includes('admin'));
      setIsTechnician(user.roles.includes('technician'));
      
      // Initialize email
      setEmail(user.email || "");
      
      // Initialize profile data
      if (user.profile) {
        setFirstName(user.profile.first_name || "");
        setLastName(user.profile.last_name || "");
        setPhone(user.profile.phone || "");
        setPhoneVerified(user.profile.phone_verified || false);
        setAvatarUrl(user.profile.avatar_url || "");
      }
    }
  }, [user]);

  // Fetch logs when Activity tab is activated
  useEffect(() => {
    if (activeTab === 'activity' && open && user) {
      fetchUserActivityLogs();
    }
  }, [activeTab, open, user]);

  const fetchUserActivityLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-activity-logs', {
        body: {
          user_id: user.id,
          limit: 20,
          offset: 0,
        },
      });

      if (error) throw error;
      setActivityLogs(data?.data || []);
    } catch (error) {
      console.error('Error fetching user activity logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Validate form data
      const validatedData = userFormSchema.parse({
        email: email,
        first_name: firstName,
        last_name: lastName,
        phone: phone || undefined,
      });

      // 2. Update email if changed
      if (validatedData.email !== user.email) {
        console.log('Updating email from', user.email, 'to', validatedData.email);
        
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        const { data, error: emailError } = await supabase.functions.invoke('update-user-email', {
          body: { 
            user_id: user.id, 
            new_email: validatedData.email 
          },
        });

        if (emailError) throw emailError;
        if (data?.error) throw new Error(data.error);
        
        console.log('Email updated successfully');
      }

      // 3. Update profile data
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: validatedData.first_name,
          last_name: validatedData.last_name,
          phone: validatedData.phone || null,
          phone_verified: phoneVerified,
          avatar_url: avatarUrl || null,
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // 4. Update roles
      const { data: currentRoles, error: fetchError } = await supabase
        .from('user_roles')
        .select('id, role')
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;

      const currentRoleNames = currentRoles?.map(r => r.role) || [];
      const newRoles = [];
      if (isAdmin) newRoles.push('admin');
      if (isTechnician) newRoles.push('technician');

      // Remove roles that are no longer selected
      const rolesToRemove = currentRoleNames.filter(role => !newRoles.includes(role));
      for (const role of rolesToRemove) {
        const roleId = currentRoles?.find(r => r.role === role)?.id;
        if (roleId) {
          const { error } = await supabase
            .from('user_roles')
            .delete()
            .eq('id', roleId);
          
          if (error) throw error;
        }
      }

      // Add new roles
      const rolesToAdd = newRoles.filter(role => !currentRoleNames.includes(role));
      for (const role of rolesToAdd) {
        const { error } = await supabase
          .from('user_roles')
          .insert({ user_id: user.id, role });
        
        if (error) throw error;
      }

      toast.success("User updated successfully");
      onUserUpdated();
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error('Error updating user:', error);
        toast.error('Failed to update user');
      }
    } finally {
      setLoading(false);
    }
  };

  const getFullName = () => {
    if (!user.profile) return "Unknown User";
    const first = user.profile.first_name || "";
    const last = user.profile.last_name || "";
    return `${first} ${last}`.trim() || "Unknown User";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update profile and roles for {getFullName()}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="mt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="roles">Roles</TabsTrigger>
              <TabsTrigger value="activity">
                <History className="mr-2 h-4 w-4" />
                Activity
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  User will need to verify their new email address
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+16195551234"
                />
                <p className="text-xs text-muted-foreground">
                  Format: +[country code][number] (e.g., +16195551234)
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="phoneVerified"
                  checked={phoneVerified}
                  onCheckedChange={(checked) => setPhoneVerified(checked as boolean)}
                />
                <Label htmlFor="phoneVerified" className="cursor-pointer">
                  Phone number verified
                </Label>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="avatarUrl">Avatar URL (Optional)</Label>
                <Input
                  id="avatarUrl"
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>
            </TabsContent>
            
            <TabsContent value="roles" className="space-y-4 mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="technician"
                  checked={isTechnician}
                  onCheckedChange={(checked) => setIsTechnician(checked as boolean)}
                />
                <Label htmlFor="technician" className="cursor-pointer">
                  <div>
                    <div className="font-medium">Technician</div>
                    <div className="text-sm text-muted-foreground">
                      Can access team portal and service resources
                    </div>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="admin"
                  checked={isAdmin}
                  onCheckedChange={(checked) => setIsAdmin(checked as boolean)}
                />
                <Label htmlFor="admin" className="cursor-pointer">
                  <div>
                    <div className="font-medium">Admin</div>
                    <div className="text-sm text-muted-foreground">
                      Full access including user management
                    </div>
                  </div>
                </Label>
              </div>
            </TabsContent>
            
            <TabsContent value="activity" className="space-y-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium">Recent Activity</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  type="button"
                  onClick={() => {
                    window.open(`/admin/activity-logs?user_id=${user.id}`, '_blank');
                  }}
                >
                  View All <ExternalLink className="ml-2 h-3 w-3" />
                </Button>
              </div>
              
              {loadingLogs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No activity logs found for this user
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {activityLogs.map((log) => (
                    <div 
                      key={log.id} 
                      className="flex items-start justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {log.action_category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm">{log.description}</p>
                        {log.actor_profile && (
                          <p className="text-xs text-muted-foreground">
                            by {log.actor_profile.first_name} {log.actor_profile.last_name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
